import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Phone, Mail, MessageCircle, CheckCircle2, Loader2, PackageCheck } from 'lucide-react';
import { api } from '../api/client';
import { useSite } from '../context/SiteContext';
import { useT } from '../context/I18nContext';

const OrderThanks = () => {
  const { orderNumber } = useParams();
  const { site } = useSite();
  const { t } = useT();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getOrder(orderNumber)
      .then(setOrder)
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderNumber]);

  const supportEmail = site?.contact?.email || '';
  const supportPhone = site?.contact?.phone || '';

  if (loading) return (<><Header /><main className="bg-black min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 text-[#d4af37] animate-spin" /></main><Footer /></>);
  if (!order) return (
    <><Header />
      <main className="bg-black min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="serif-italic gold-gradient-text text-4xl">{t('thanks.not_found')}</h1>
          <p className="text-zinc-400 mt-2">{t('thanks.not_found_hint')}</p>
          <Link to="/" className="text-[#d4af37] mt-4 inline-block underline">{t('nav.home')}</Link>
        </div>
      </main>
      <Footer />
    </>
  );

  return (
    <>
      <Header />
      <main className="bg-black min-h-[60vh]">
        <div className="max-w-[900px] mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <div className="w-20 h-20 rounded-full border-2 border-[#d4af37] mx-auto flex items-center justify-center mb-5 bg-[#d4af37]/10">
              <CheckCircle2 className="h-10 w-10 text-[#d4af37]" />
            </div>
            <p className="text-[#d4af37] tracking-[0.3em] text-xs uppercase mb-2">{t('thanks.eyebrow')}</p>
            <h1 className="serif-italic gold-gradient-text text-5xl" data-testid="thanks-order-number">{order.order_number}</h1>
            <p className="text-zinc-300 mt-4 max-w-lg mx-auto leading-relaxed" data-testid="thanks-message">
              {t('thanks.intro_a')} <strong className="text-zinc-100">{order.shipping?.first_name}</strong>{t('thanks.intro_b')}
            </p>
            <Badge variant="outline" className="border-amber-500 text-amber-400 text-sm px-3 py-1.5 mt-4">{t('thanks.status')}</Badge>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <Card className="bg-zinc-950 border-zinc-900 p-5">
              <h3 className="serif-italic text-xl text-[#d4af37] mb-3 flex items-center gap-2"><PackageCheck className="h-5 w-5" /> {t('thanks.next_steps')}</h3>
              <ul className="space-y-2 text-sm text-zinc-300 list-disc list-inside">
                <li>{t('thanks.step_call')}</li>
                <li>{t('thanks.step_confirm')}</li>
                <li>{t('thanks.step_ship')}</li>
              </ul>
            </Card>

            <Card className="bg-zinc-950 border-zinc-900 p-5">
              <h3 className="serif-italic text-xl text-[#d4af37] mb-3">{t('thanks.contact_us')}</h3>
              <div className="space-y-2 text-sm">
                {supportPhone && <a href={`tel:${supportPhone}`} className="flex items-center gap-2 text-zinc-300 hover:text-[#d4af37]"><Phone className="h-4 w-4" /> {supportPhone}</a>}
                {supportEmail && <a href={`mailto:${supportEmail}?subject=Order ${order.order_number}`} className="flex items-center gap-2 text-zinc-300 hover:text-[#d4af37]"><Mail className="h-4 w-4" /> {supportEmail}</a>}
                <p className="text-zinc-500 text-xs pt-2">{t('thanks.reference')} <span className="font-mono text-[#d4af37]">{order.order_number}</span></p>
              </div>
            </Card>
          </div>

          <Card className="bg-zinc-950 border-zinc-900 p-5 mt-5">
            <h3 className="serif-italic text-xl text-[#d4af37] mb-3">{t('voucher.summary')}</h3>
            <ul className="space-y-1.5 text-sm text-zinc-300">
              {order.items.map((i, idx) => (
                <li key={`${i.product_id || 'item'}-${idx}`} className="flex justify-between"><span>{i.name} × {i.qty}</span><span>€{(i.price * i.qty).toFixed(2)}</span></li>
              ))}
            </ul>
            <div className="border-t border-zinc-800 mt-3 pt-3 space-y-1 text-sm">
              <div className="flex justify-between text-zinc-400"><span>{t('checkout.subtotal')}</span><span>€{order.subtotal.toFixed(2)}</span></div>
              {order.shipping_cost > 0 && <div className="flex justify-between text-zinc-400"><span>{t('checkout.shipping')}</span><span>€{order.shipping_cost.toFixed(2)}</span></div>}
              <div className="flex justify-between text-zinc-100 pt-2 border-t border-zinc-800 mt-2">
                <span className="text-base">{t('checkout.total')}</span>
                <span className="text-[#d4af37] text-xl" data-testid="thanks-total">€{order.total.toFixed(2)}</span>
              </div>
            </div>
          </Card>

          <div className="text-center mt-10">
            <Link to="/shop"><Button variant="outline" className="btn-outline-gold">{t('cart.continue')}</Button></Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default OrderThanks;
